/**
 * When a realtime store reads itself again: at the moment the server says the Postgres change
 * stream of the channel exists (app#439).
 *
 * A store reads again after each join, because an event that is sent while the channel is down is
 * gone (app#366, app#437). ⚠⚠ `SUBSCRIBED` IS NOT THAT MOMENT. It is the answer to the join, and
 * the server starts the change stream AFTER it answers. It then sends the system message
 * `{ extension: 'postgres_changes', status: 'ok' }` ("Subscribed to PostgreSQL"). A read that is
 * sent on `SUBSCRIBED` can be answered before the stream exists, and a row that changes between
 * that answer and the start of the stream gives no event and no later read.
 *
 * MEASURED on the hosted project, 2026-10-01, 22 joins: the message arrived each time, 3 to 16 ms
 * after `SUBSCRIBED` in 21 of them and 1,971 ms after it in one (the server tries again after 1 to
 * 3 s when its database connection is not ready, and after 5 to 10 s on an error).
 *
 * So the read is made on the system message. A read that is sent then has its answer made after
 * the stream started, so each later change gives an event.
 *
 * ⚠ THE FALLBACK. If the message does not arrive within `STREAM_START_FALLBACK_MS` of a join,
 * the store reads anyway, and it reads again when the message arrives. So a server that stops
 * sending the message still gets one read for each join, `STREAM_START_FALLBACK_MS` later than
 * the read on `SUBSCRIBED` was. A status that is not `SUBSCRIBED` does NOT cancel the fallback:
 * a channel that drops and joins again in less than that time would then never read.
 *
 * ⚠⚠ THE STORE READS ON EVERY SUCH MESSAGE, not only on the first one after a join. The server
 * sends it each time it makes the subscription again: after its own change stream restarts (a
 * real gap, with no rejoin to show it), and after an access-token refresh (no gap). The two look
 * the same to the client. The client half of the refresh case is confirmed (it sends the new token
 * on each joined channel); the server's second message is read from the upstream source and is NOT
 * measured on the hosted project.
 * ⚠ THE COST OF THAT, for `useNode`: each read applies the offline filter again. A node that the
 * store holds, and that has passed OFFLINE_NODE_VISIBILITY_HOURS offline since it was loaded,
 * leaves the store at the next read, as it does on a reload. Before app#439 that happened only
 * on a channel rejoin.
 */
export const STREAM_START_FALLBACK_MS = 5_000

export function readWhenStreamStarts(read: () => void) {
  let fallback: ReturnType<typeof setTimeout> | undefined
  let stopped = false
  const clear = () => {
    clearTimeout(fallback)
    fallback = undefined
  }
  return {
    /** The channel's subscribe callback. */
    onStatus: (status: string) => {
      // One fallback for each wait: a second join before the message does not start it again.
      if (stopped || status !== 'SUBSCRIBED' || fallback !== undefined) return
      fallback = setTimeout(() => {
        fallback = undefined
        if (!stopped) read()
      }, STREAM_START_FALLBACK_MS)
    },
    /** The channel's `system` listener. */
    onSystem: (payload: { extension?: string; status?: string }) => {
      if (stopped || payload?.extension !== 'postgres_changes' || payload.status !== 'ok') return
      clear()
      read()
    },
    /** For the store's unsubscribe: no read after it, from the fallback or from a late message. */
    stop: () => {
      stopped = true
      clear()
    },
  }
}
